/**
 * analysis.* handler for BridgeHost, backed by MISpatialAnalysis (Turf).
 * Engine-free module: safe to import in Node (conformance hosts, tests)
 * without dragging in MapLibre/Cesium.
 *
 * Geometry-producing ops return { geojson }; scalar ops return typed
 * fields (spec §2.5).
 */
import { BridgeMethodError, ERR_METHOD_NOT_FOUND, ERR_INVALID_PARAMS, ERR_ENGINE } from './protocol';

function req<T>(v: T | undefined | null, name: string): T {
  if (v === undefined || v === null) throw new BridgeMethodError(ERR_INVALID_PARAMS, `missing param '${name}'`);
  return v;
}

function toPlain(value: any): any {
  try { return JSON.parse(JSON.stringify(value ?? null)); } catch { return null; }
}

export function createSpatialAnalysisEngine(): (op: string, p: Record<string, any>) => unknown {
  // Lazy import keeps Turf out of hosts that never use analysis.*.
  let engine: any = null;
  const load = async () => {
    if (!engine) engine = (await import('../core/SpatialAnalysis')).MISpatialAnalysis;
    return engine;
  };

  return async (op: string, p: Record<string, any>) => {
    const A = await load();
    const units = p.units ?? 'kilometers';
    const g = (r: any) => ({ geojson: toPlain(r) });
    try {
      switch (op) {
        case 'buffer': return g(A.buffer(req(p.geojson, 'geojson'), req(p.radius, 'radius'), { units }));
        case 'area': return { areaM2: A.area(req(p.geojson, 'geojson')) };
        case 'length': return { length: A.getLength(req(p.geojson, 'geojson'), units) };
        case 'distance': return { distance: A.distance(req(p.from, 'from'), req(p.to, 'to'), units) };
        case 'centroid': return g(A.centroid(req(p.geojson, 'geojson')));
        case 'bbox': return { bbox: A.bbox(req(p.geojson, 'geojson')) };
        case 'simplify': return g(A.simplify(req(p.geojson, 'geojson'), p.tolerance ?? 0.01));
        case 'union': return g(A.union(req(p.a, 'a'), req(p.b, 'b')));
        case 'intersection': return g(A.intersection(req(p.a, 'a'), req(p.b, 'b')));
        case 'difference': return g(A.difference(req(p.a, 'a'), req(p.b, 'b')));
        case 'convexHull': return g(A.convexHull(req(p.geojson, 'geojson')));
        case 'along': return g(A.along(req(p.line, 'line'), req(p.distance, 'distance'), units));
        case 'bearing': return { bearing: A.bearing(req(p.from, 'from'), req(p.to, 'to')) };
        case 'destination': return g(A.destination(req(p.from, 'from'), req(p.distance, 'distance'), req(p.bearing, 'bearing'), units));
        case 'midpoint': return g(A.midpoint(req(p.a, 'a'), req(p.b, 'b')));
        case 'booleanContains': return { value: !!A.booleanContains(req(p.a, 'a'), req(p.b, 'b')) };
        case 'booleanIntersects': return { value: !!A.booleanIntersects(req(p.a, 'a'), req(p.b, 'b')) };
        case 'pointsWithinPolygon': return g(A.pointsWithinPolygon(req(p.points, 'points'), req(p.polygon, 'polygon')));
        default:
          throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown analysis op '${op}'`);
      }
    } catch (e) {
      if (e instanceof BridgeMethodError) throw e;
      throw new BridgeMethodError(ERR_ENGINE, `analysis.${op} failed: ${(e as Error).message}`);
    }
  };
}
